import isEmpty from 'lodash.isempty';
import get from 'lodash.get';
import first from 'lodash.first';
import last from 'lodash.last';
import zip from 'lodash.zip';
import compact from 'lodash.compact';

import {
  DEFAULT_EXCHANGE,
  DEFAULT_MINER,
  RESOLUTION_10MIN,
  RESOLUTION_1H,
  RESOLUTION_1M,
  RESOLUTION_1W,
  RESOLUTION_24H,
  RESOLUTION_MAX_RANGE_10MIN,
  TIER_1,
  TIER_2,
} from '../../constants/app';
import {
  MOVING_MATH_HOURS_OPTIONS,
  MOVING_MATH_MINUTES_OPTIONS,
  MOVING_MATH_DAYS_OPTIONS,
  MOVING_MATH_WEEKS_OPTIONS,
  MOVING_MATH_MONTHS_OPTIONS,
  MOVING_MATH_VALID_RANGE,
  MOVING_MATH_MINUTES_VALID_RANGE,
} from '../../constants/shared';
import { MAX_TRACES_NUMBER } from '../../constants/compare';

import * as endpointsSelectors from '../endpoints/selectors';
import * as routerSelectors from '../router/selectors';
import { selectUserPlans } from '../auth/user/selectors';

import {
  createDeepEqualSelector,
  getResolutionUnit,
  getMetricConfig,
  affixForExchange,
  affixForMiner,
} from '../utils/selectors';
import { isMetricLocked } from '../../utils/other';
import { convertDurationToRange } from '../../utils/time';

const selectCompare = (state) => state.compare;

export const selectTraces = createDeepEqualSelector(selectCompare, (compare) => compare.traces);

export const selectTracesLen = createDeepEqualSelector(selectTraces, (traces) => traces.length);

export const selectTracesIdx = createDeepEqualSelector(selectTracesLen, (len) => [...Array(len)].map((_, idx) => idx));

export const selectHasMaxTracesNumber = createDeepEqualSelector(selectTracesLen, (len) => len >= MAX_TRACES_NUMBER);

export const selectMetricsAssets = createDeepEqualSelector(selectTraces, (traces) => traces.map((item) => item.asset));

export const selectMetricsCodes = createDeepEqualSelector(selectTraces, (traces) =>
  traces.map((item) => item.metricCode),
);

export const selectMetricsColors = createDeepEqualSelector(selectTraces, (traces) => traces.map((item) => item.color));

export const selectMetricsCurrencies = createDeepEqualSelector(selectTraces, (traces) =>
  traces.map((item) => item.currency),
);

export const selectMetricsExchanges = createDeepEqualSelector(selectTraces, (traces) =>
  traces.map((item) => item.exchange),
);

export const selectMetricsMiners = createDeepEqualSelector(selectTraces, (traces) => traces.map((item) => item.miner));

export const selectMetricsResolutions = createDeepEqualSelector(selectTraces, (traces) =>
  traces.map((item) => item.resolution),
);

export const selectMetricsScales = createDeepEqualSelector(selectTraces, (traces) => traces.map((item) => item.scale));

export const selectMovingAverages = createDeepEqualSelector(selectTraces, (traces) =>
  traces.map((item) => item.movingAverage),
);

export const selectMovingMedians = createDeepEqualSelector(selectTraces, (traces) =>
  traces.map((item) => item.movingMedian),
);

export const selectMetricsData = createDeepEqualSelector(selectTraces, (traces) => traces.map((item) => item.data));

export const selectMetricsLoading = createDeepEqualSelector(selectTraces, (traces) =>
  traces.some((item) => item.loading),
);

export const selectMetricsErrors = createDeepEqualSelector(selectTraces, (traces) => traces.some((item) => item.err));

export const selectMetricsRange = createDeepEqualSelector(selectTraces, (traces) => {
  const ranges = traces
    .map((item) => {
      const f = get(first(item.data), 't', null);
      const l = get(last(item.data), 't', null);

      return [f, l];
    })
    .reduce((acc, c) => acc.concat(c), [])
    .filter((item) => item !== null);

  if (isEmpty(ranges)) {
    return [];
  }

  return [Math.min(...ranges), Math.max(...ranges)];
});

export const selectMetricsDependencyData = createDeepEqualSelector(selectTraces, (traces) =>
  traces.map((item) => get(item, 'dependency.data', {})),
);

export const selectMetricsDependencyDataIsLoading = createDeepEqualSelector(selectTraces, (traces) =>
  traces.map((item) => get(item, 'dependency.loading', false)),
);

export const selectMetricsDependencyDataErr = createDeepEqualSelector(selectTraces, (traces) =>
  traces.map((item) => get(item, 'dependency.err', false)),
);

export const selectComputedMetricsCodes = createDeepEqualSelector(
  [routerSelectors.selectMetricsCodes, selectMetricsCodes, selectTracesLen],
  (metricsCodesInUrl, metricsCodes, tracesLen) => {
    if (isEmpty(metricsCodesInUrl)) {
      return metricsCodes;
    }

    if (metricsCodesInUrl.length !== tracesLen) {
      return metricsCodes;
    }

    return metricsCodesInUrl;
  },
);

export const selectMetricsConfigs = createDeepEqualSelector(selectComputedMetricsCodes, (metricsCodes) =>
  metricsCodes.map((code) => getMetricConfig(code)),
);

export const selectComputedMetricsAssets = createDeepEqualSelector(
  [routerSelectors.selectAssets, selectMetricsAssets, selectTracesLen],
  (assetsInUrl, assets, tracesLen) => {
    if (isEmpty(assetsInUrl)) {
      return assets;
    }

    if (assetsInUrl.length !== tracesLen) {
      return assets;
    }

    return assetsInUrl;
  },
);

export const selectComputedCurrencies = createDeepEqualSelector(
  [routerSelectors.selectCurrencies, selectMetricsCurrencies, selectTracesLen],
  (currenciesInUrl, currencies, tracesLen) => {
    if (isEmpty(currenciesInUrl)) {
      return currencies;
    }

    if (currenciesInUrl.length !== tracesLen) {
      return currencies;
    }

    return currenciesInUrl;
  },
);

export const selectComputedExchanges = createDeepEqualSelector(
  [routerSelectors.selectExchanges, selectMetricsExchanges, selectTracesLen],
  (exchangesInUrl, exchanges, tracesLen) => {
    if (isEmpty(exchangesInUrl)) {
      return exchanges;
    }

    if (exchangesInUrl.length !== tracesLen) {
      return exchanges;
    }

    return exchangesInUrl;
  },
);

const selectCurrentEndpointAssets = createDeepEqualSelector(
  [endpointsSelectors.selectEndpointsData, selectMetricsConfigs, selectTracesIdx],
  (endpointsData, configs, tracesIdx) =>
    tracesIdx.map((idx) => {
      const path = get(configs[idx], 'path');

      return get(endpointsData, `${path}.assets`, []);
    }),
);

export const selectValidatedExchanges = createDeepEqualSelector(
  [selectComputedExchanges, selectComputedMetricsAssets, selectCurrentEndpointAssets, selectTracesIdx],
  (exchanges, assets, currentEndpointAssets, tracesIdx) =>
    tracesIdx.map((idx) => {
      const asset = get(assets, `${idx}`);
      const exchange = get(exchanges, `${idx}`);
      const endpointsAssets = get(currentEndpointAssets, `${idx}`);

      const assetData = endpointsAssets.find((a) => a.symbol === asset);

      const validExchanges = get(assetData, 'exchanges', []);
      const isValid = validExchanges.includes(exchange);

      if (isValid) {
        return exchange;
      }

      return DEFAULT_EXCHANGE;
    }),
);

export const selectComputedMiners = createDeepEqualSelector(
  [routerSelectors.selectMiners, selectMetricsMiners, selectTracesLen],
  (minersInUrl, miners, tracesLen) => {
    if (isEmpty(minersInUrl)) {
      return miners;
    }

    if (minersInUrl.length !== tracesLen) {
      return miners;
    }

    return minersInUrl;
  },
);

export const selectComputedMetricsScales = createDeepEqualSelector(
  [routerSelectors.selectMetricsScales, selectMetricsScales, selectTracesLen],
  (metricsScalesInUrl, metricsScales, tracesLen) => {
    if (isEmpty(metricsScalesInUrl)) {
      return metricsScales;
    }

    if (metricsScalesInUrl.length !== tracesLen) {
      return metricsScales;
    }

    return metricsScalesInUrl;
  },
);

export const selectComputedMovingAverages = createDeepEqualSelector(
  [routerSelectors.selectMovingAverages, selectMovingAverages, selectTracesLen],
  (movingAveragesInUrl, movingAverages, tracesLen) => {
    if (isEmpty(movingAveragesInUrl)) {
      return movingAverages;
    }

    if (movingAveragesInUrl.length !== tracesLen) {
      return movingAverages;
    }

    return movingAveragesInUrl;
  },
);

export const selectComputedMovingMedians = createDeepEqualSelector(
  [routerSelectors.selectMovingMedians, selectMovingMedians, selectTracesLen],
  (movingMediansInUrl, movingMedians, tracesLen) => {
    if (isEmpty(movingMediansInUrl)) {
      return movingMedians;
    }

    if (movingMediansInUrl.length !== tracesLen) {
      return movingMedians;
    }

    return movingMediansInUrl;
  },
);

export const selectMetricsDataInRange = createDeepEqualSelector(
  [routerSelectors.selectSince, routerSelectors.selectUntil, selectTraces],
  (since, until, traces) => {
    if (!since && !until) {
      return [];
    }

    const currentRangeData = traces.map((trace) =>
      trace.data.filter((item) => {
        const time = get(item, 't', null);
        if (time < since || time > until) {
          return false;
        }

        return true;
      }),
    );

    return currentRangeData;
  },
);

export const selectComputedMetricsRange = createDeepEqualSelector([selectMetricsDataInRange], (data) => {
  const metricsRanges = data
    .map((trace) => [get(first(trace), 't'), get(last(trace), 't')])
    .reduce((acc, c) => acc.concat(c), [])
    .filter((item) => !!item);

  if (isEmpty(metricsRanges)) {
    return [];
  }

  return [Math.min(...metricsRanges), Math.max(...metricsRanges)];
});

export const selectMetricsResolutionOptions = createDeepEqualSelector(
  [selectTracesIdx, selectMetricsConfigs],
  (tracesIdx, metricsConfigs) =>
    tracesIdx.map((idx) => {
      const config = metricsConfigs[idx];

      return get(config, 'resolutionOption', false);
    }),
);

export const selectMetricsTitlesArray = createDeepEqualSelector(
  [
    selectTracesIdx,
    selectComputedMetricsAssets,
    endpointsSelectors.selectAssets,
    selectMetricsConfigs,
    selectComputedExchanges,
    selectComputedMiners,
  ],
  (tracesIdx, assets, allAssets, metricsConfigs, exchanges, miners) => {
    const a = [];
    const m = [];

    tracesIdx.forEach((idx) => {
      const exchange = exchanges[idx] || DEFAULT_EXCHANGE;
      const miner = miners[idx] || DEFAULT_MINER;

      const assetData = allAssets.find((ast) => ast.symbol === assets[idx]);
      const asset = get(assetData, 'name', assets[idx]);
      const metricCode = get(metricsConfigs[idx], 'code');
      const exchangeOption = get(metricsConfigs[idx], 'exchangeOption', false);
      const minerOption = get(metricsConfigs[idx], 'minerOption', false);
      const fullName = get(metricsConfigs[idx], 'name', metricCode);

      const exchangeAffix = affixForExchange(exchangeOption, exchange);
      const minerAffix = affixForMiner(minerOption, miner);

      const name = compact([fullName, exchangeAffix, minerAffix]).join(' ');

      a.push(asset);
      m.push(name);
    });

    return [a, m];
  },
);

export const selectCustomDenominations = createDeepEqualSelector(
  [selectTracesIdx, selectMetricsConfigs, selectComputedMetricsAssets],
  (tracesIdx, metricsConfigs, asset) =>
    tracesIdx.map((idx) => {
      const denominations = get(metricsConfigs[idx], 'useDenominations', {});

      return get(denominations, `${asset[idx]}`, {});
    }),
);

export const selectCurrencyLabels = createDeepEqualSelector(
  [selectTracesIdx, selectComputedMetricsAssets, selectCustomDenominations],
  (tracesIdx, metricsAssets, denominations) =>
    tracesIdx.map((idx) => get(denominations[idx], 'name', metricsAssets[idx])),
);

export const selectAssetsAvailable = createDeepEqualSelector(
  [selectTracesIdx, selectComputedMetricsAssets, selectMetricsConfigs, endpointsSelectors.selectEndpointsData],
  (tracesIdx, metricsAssets, metricsConfigs, endpointsData) =>
    tracesIdx.map((idx) => {
      const metricConfig = metricsConfigs[idx];
      const path = get(metricConfig, 'path', '');
      const availableAssets = get(endpointsData, `[${path}].assets`, []);

      const currentAssetData = availableAssets.find((a) => a.symbol === metricsAssets[idx]);

      const available = !isEmpty(currentAssetData);

      return available;
    }),
);

export const selectMetricsLocked = createDeepEqualSelector(
  [selectTracesIdx, selectMetricsConfigs, selectUserPlans],
  (tracesIdx, metricsConfigs, userPlans) =>
    tracesIdx.map((idx) => {
      const config = metricsConfigs[idx];
      const tier = get(config, 'tier', TIER_1);
      const isNew = get(config, 'isNew', false);

      if (isNew) {
        return false;
      }

      return isMetricLocked(tier, userPlans);
    }),
);

export const selectAnyMetricsLocked = createDeepEqualSelector(selectMetricsLocked, (metricsLocked) =>
  metricsLocked.includes(true),
);

export const selectMetricsTiers = createDeepEqualSelector(
  [selectTracesIdx, selectMetricsConfigs],
  (tracesIdx, metricsConfigs) =>
    tracesIdx.map((idx) => {
      const config = metricsConfigs[idx];
      const tier = get(config, 'tier', TIER_1);

      return tier;
    }),
);

export const selectChartTitle = createDeepEqualSelector(selectMetricsTitlesArray, (metricsTitlesArray) => {
  const assets = metricsTitlesArray[0];
  const metrics = metricsTitlesArray[1];

  return zip(assets, metrics)
    .map((item) => item.filter((v) => v))
    .map((item) => item.join(': '))
    .join(' vs. ');
});

export const selectComputedResolutions = createDeepEqualSelector(
  [routerSelectors.selectResolutions, selectMetricsResolutions, selectTracesLen],
  (resolutionsInUrl, resolutions, tracesLen) => {
    if (isEmpty(resolutionsInUrl)) {
      return resolutions;
    }

    if (resolutionsInUrl.length !== tracesLen) {
      return resolutions;
    }

    return resolutionsInUrl;
  },
);

export const selectEndpointsResolutions = createDeepEqualSelector(
  [selectTracesIdx, selectMetricsConfigs, endpointsSelectors.selectEndpointsData],
  (tracesIdx, metricsConfigs, endpointsData) =>
    tracesIdx.map((idx) => {
      const config = metricsConfigs[idx];
      const path = get(config, 'path', '');
      const resolutions = get(endpointsData, `${path}.resolutions`, []);

      return resolutions;
    }),
);

export const selectAreResolutionsAvailable = createDeepEqualSelector(
  [selectTracesIdx, selectMetricsConfigs, selectEndpointsResolutions, selectComputedResolutions],
  (tracesIdx, metricsConfigs, endpointsResolutions, resolutions) =>
    tracesIdx.map((idx) => {
      const endpointResolution = endpointsResolutions[idx];
      const resolution = resolutions[idx];
      const metricConfig = metricsConfigs[idx];
      const resolutionOption = get(metricConfig, 'resolutionOption', false);

      if (isEmpty(endpointResolution)) {
        return false;
      }

      if (!resolutionOption) {
        return false;
      }

      if (endpointResolution.includes(resolution)) {
        return true;
      }

      return false;
    }),
);

export const selectAreResolutionsAllowed = createDeepEqualSelector(
  [selectTracesIdx, selectUserPlans, selectMetricsTiers, selectComputedResolutions],
  (tracesIdx, userPlans, metricsTiers, resolutions) =>
    tracesIdx.map((idx) => {
      const { isUserAdvanced, isUserProfessional } = userPlans;
      const resolution = resolutions[idx];
      const metricTier = metricsTiers[idx];

      if (resolution === RESOLUTION_1M) {
        return true;
      }

      if (resolution === RESOLUTION_1W) {
        return true;
      }

      if (resolution === RESOLUTION_24H) {
        return true;
      }

      if (resolution === RESOLUTION_1H) {
        if (isUserProfessional) {
          return true;
        }

        if (isUserAdvanced && metricTier < TIER_2) {
          return true;
        }
      }

      if (resolution === RESOLUTION_10MIN) {
        if (isUserProfessional) {
          return true;
        }
      }

      return false;
    }),
);

export const selectAreResolutionsValid = createDeepEqualSelector(
  [selectTracesIdx, selectAreResolutionsAvailable, selectAreResolutionsAllowed],
  (tracesIdx, areResolutionsAvailable, areResolutionsAllowed) =>
    tracesIdx.map((idx) => {
      const isResolutionAvailable = areResolutionsAvailable[idx];
      const isResolutionAllowed = areResolutionsAllowed[idx];

      if (isResolutionAvailable && isResolutionAllowed) {
        return true;
      }

      return false;
    }),
);

export const selectValidatedResolutions = createDeepEqualSelector(
  [selectTracesIdx, selectAreResolutionsValid, selectComputedResolutions],
  (tracesIdx, areResolutionsValid, resolutions) =>
    tracesIdx.map((idx) => {
      const isResolutionValid = areResolutionsValid[idx];
      const resolution = resolutions[idx];
      if (isResolutionValid) {
        return resolution;
      }

      return RESOLUTION_24H;
    }),
);

export const selectMetricsStart = (resolution) => {
  if (resolution === RESOLUTION_10MIN) {
    const [since] = convertDurationToRange(RESOLUTION_MAX_RANGE_10MIN);

    return since;
  }
};

export const makeSelectMovingMathOptions = (idx) =>
  createDeepEqualSelector([selectValidatedResolutions], (resolutions) => {
    const resolution = resolutions[idx];
    if (resolution === RESOLUTION_10MIN) {
      return MOVING_MATH_MINUTES_OPTIONS;
    }

    if (resolution === RESOLUTION_1H) {
      return MOVING_MATH_HOURS_OPTIONS;
    }

    if (resolution === RESOLUTION_1W) {
      return MOVING_MATH_WEEKS_OPTIONS;
    }

    if (resolution === RESOLUTION_1M) {
      return MOVING_MATH_MONTHS_OPTIONS;
    }

    return MOVING_MATH_DAYS_OPTIONS;
  });

export const makeSelectMovingMathValidRange = (idx) =>
  createDeepEqualSelector([selectValidatedResolutions], (resolutions) => {
    const resolution = resolutions[idx];
    if (resolution === RESOLUTION_10MIN) {
      return MOVING_MATH_MINUTES_VALID_RANGE;
    }

    return MOVING_MATH_VALID_RANGE;
  });

export const makeSelectMovingMathUnit = (idx) =>
  createDeepEqualSelector([selectValidatedResolutions], (resolutions) => {
    const resolution = resolutions[idx];

    return getResolutionUnit(resolution);
  });
